/**
 * App shell + router.
 *
 * Routing strategy:
 *
 *   - AuthProvider runs once at the root and fetches /me on mount.
 *   - While loading, all routes show a placeholder so we don't flash
 *     /login on a still-valid session.
 *   - Once /me settles, route guards (`<RequireFullSession>`,
 *     `<RequireSemiAuth>`, `<RequirePublic>`) decide where to send the
 *     operator. Half-session pages (/change-password, /totp/enroll,
 *     /totp/verify) are gated explicitly so an unauthenticated user
 *     can't load them and an enrolled user can't bypass the password
 *     gate by deep-linking.
 *   - A single window-level listener for `senti:auth-revoked` (raised
 *     by the api.ts response interceptor on 401) bumps the user back
 *     to /login.
 */
import { useEffect, useRef, type ReactNode } from "react";
import {
  Navigate,
  RouterProvider,
  createBrowserRouter,
  useLocation,
  useNavigate,
  Outlet,
} from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/lib/auth-context";
import { useAuth } from "@/lib/use-auth";
import { AUTH_REVOKED_EVENT } from "@/lib/api";
import { buildLoginRedirect, isPublicAuthPath } from "@/lib/redirect";
import { queryClient } from "@/lib/query-client";
import { RequireSuperAdmin } from "@/lib/guards";
import { ThemeProvider } from "@/lib/theme-context";
import { AppLayout } from "@/components/shell/AppLayout";
import LoginPage from "@/pages/Login";
import ChangePasswordPage from "@/pages/ChangePassword";
import ForgotPasswordPage from "@/pages/ForgotPassword";
import ResetPasswordPage from "@/pages/ResetPassword";
import TOTPEnrollPage from "@/pages/TOTPEnroll";
import TOTPVerifyPage from "@/pages/TOTPVerify";
import DashboardPage from "@/pages/Dashboard";
import TenantsListPage from "@/pages/tenants/TenantsList";
import TenantNewPage from "@/pages/tenants/TenantNew";
import TenantDetailPage from "@/pages/tenants/TenantDetail";
import OperatorsListPage from "@/pages/operators/OperatorsList";
import OperatorNewPage from "@/pages/operators/OperatorNew";
import OperatorDetailPage from "@/pages/operators/OperatorDetail";
import PaymentIntentsListPage from "@/pages/payments/PaymentIntentsList";
import PaymentIntentDetailPage from "@/pages/payments/PaymentIntentDetail";
import InboundPaymentsListPage from "@/pages/payments/InboundPaymentsList";
import InboundPaymentDetailPage from "@/pages/payments/InboundPaymentDetail";
import CallbackInboxListPage from "@/pages/observability/CallbackInboxList";
import CallbackInboxDetailPage from "@/pages/observability/CallbackInboxDetail";
import WebhookOutboxListPage from "@/pages/observability/WebhookOutboxList";
import WebhookOutboxDetailPage from "@/pages/observability/WebhookOutboxDetail";
import AuditLogListPage from "@/pages/observability/AuditLogList";
import BalanceQueriesListPage from "@/pages/observability/BalanceQueriesList";
import BalanceQueryDetailPage from "@/pages/observability/BalanceQueryDetail";

function LoadingScreen() {
  return (
    <main className="flex min-h-screen flex-col items-center justify-center gap-3 bg-background text-sm text-muted-foreground">
      <div
        aria-hidden
        className="flex h-9 w-9 items-center justify-center rounded-md bg-primary text-primary-foreground"
      >
        <svg
          viewBox="0 0 24 24"
          className="h-5 w-5 animate-pulse"
          fill="none"
          stroke="currentColor"
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M4 17l6-6 4 4 6-9" />
        </svg>
      </div>
      <span className="sr-only">Loading</span>
    </main>
  );
}

/** Routes mounted under this shell get the global 401 listener. */
function RootShell() {
  const navigate = useNavigate();
  const location = useLocation();
  // Track location through a ref so the window listener has the
  // current path without re-binding on every navigation. Re-binding on
  // each nav opens a (small) gap between cleanup and re-add during
  // which an `auth-revoked` event would be lost.
  const locationRef = useRef(location);
  locationRef.current = location;
  useEffect(() => {
    function onRevoked() {
      if (isPublicAuthPath(locationRef.current.pathname)) return;
      const here = `${locationRef.current.pathname}${locationRef.current.search}`;
      navigate(buildLoginRedirect(here), { replace: true });
    }
    window.addEventListener(AUTH_REVOKED_EVENT, onRevoked);
    return () => window.removeEventListener(AUTH_REVOKED_EVENT, onRevoked);
  }, [navigate]);
  return <Outlet />;
}

export function RequireFullSession({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) {
    return <Navigate to="/change-password" replace />;
  }
  if (!user.totp_enrolled) {
    return <Navigate to="/totp/enroll" replace />;
  }
  return <>{children}</>;
}

/**
 * Gate for the half-session pages — operator IS authenticated but
 * still mid-flow (must change password and/or enroll TOTP).
 *
 * `needs` declares which step this page expects the operator to be on.
 * If they're at the wrong step, redirect them to the correct one
 * rather than rendering a page that would either 401 or 409 against
 * the server.
 *
 * Special case: `change_password` is also reachable from a fully
 * authenticated session (e.g. a future "change password" link in the
 * dashboard). We allow that to keep the future surface working without
 * a separate route.
 */
export function RequireSemiAuth({
  children,
  needs,
}: {
  children: ReactNode;
  needs: "change_password" | "totp_enroll" | "totp_verify";
}) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;

  // ``user`` is null in two distinct situations:
  //
  //   1. Genuinely unauthenticated (no session cookie).
  //   2. Fresh half-session: ``POST /auth/login`` just minted a
  //      session with ``requires_totp=true``. ``GET /me`` is gated by
  //      ``IsAuthenticatedAdmin`` and 403s on half-sessions, so
  //      ``refresh()`` swallows the error and leaves ``user`` null.
  //
  // From the SPA there's no JS-readable signal that distinguishes
  // (1) from (2) — the session cookie is ``HttpOnly``. We render the
  // half-session page either way: a genuinely unauthenticated visitor
  // who deep-linked here will submit the form, get 401 from the
  // server, and the global ``auth-revoked`` listener will bounce them
  // to /login. A legitimate half-session visitor proceeds normally.
  if (!user) return <>{children}</>;

  if (needs === "change_password") {
    // Voluntary or required — both fine; keep rendering.
    return <>{children}</>;
  }

  if (user.must_change_password) {
    // Password change is the gate that comes first; force the operator
    // through it before TOTP.
    return <Navigate to="/change-password" replace />;
  }

  if (needs === "totp_enroll") {
    if (user.totp_enrolled) {
      // Already enrolled and password-OK → fully provisioned, send
      // home. (Not /totp/verify — they've already completed auth.)
      return <Navigate to="/" replace />;
    }
    return <>{children}</>;
  }

  // needs === "totp_verify"
  if (!user.totp_enrolled) {
    return <Navigate to="/totp/enroll" replace />;
  }
  return <>{children}</>;
}

function RequirePublic({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (user && !user.must_change_password && user.totp_enrolled) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

const router = createBrowserRouter([
  {
    element: <RootShell />,
    children: [
      {
        path: "/login",
        element: (
          <RequirePublic>
            <LoginPage />
          </RequirePublic>
        ),
      },
      // Self-service reset (spec 0009 §5.8). Deliberately unguarded: a
      // signed-in operator following an emailed link must still land on
      // the page, and neither page depends on session state.
      { path: "/forgot-password", element: <ForgotPasswordPage /> },
      { path: "/reset-password", element: <ResetPasswordPage /> },
      {
        path: "/change-password",
        element: (
          <RequireSemiAuth needs="change_password">
            <ChangePasswordPage />
          </RequireSemiAuth>
        ),
      },
      {
        path: "/totp/enroll",
        element: (
          <RequireSemiAuth needs="totp_enroll">
            <TOTPEnrollPage />
          </RequireSemiAuth>
        ),
      },
      {
        path: "/totp/verify",
        element: (
          <RequireSemiAuth needs="totp_verify">
            <TOTPVerifyPage />
          </RequireSemiAuth>
        ),
      },
      {
        // Authenticated app shell — wraps every full-session route in
        // the sidebar/topbar chrome (spec 0006 §5.4).
        element: (
          <RequireFullSession>
            <AppLayout />
          </RequireFullSession>
        ),
        children: [
          { path: "/", element: <DashboardPage /> },
          { path: "/tenants", element: <TenantsListPage /> },
          {
            path: "/tenants/new",
            element: (
              <RequireSuperAdmin>
                <TenantNewPage />
              </RequireSuperAdmin>
            ),
          },
          { path: "/tenants/:id", element: <TenantDetailPage /> },
          // Operators / admin-users (spec 0006 §5.1 second half — PR-4).
          { path: "/operators", element: <OperatorsListPage /> },
          {
            path: "/operators/new",
            element: (
              <RequireSuperAdmin>
                <OperatorNewPage />
              </RequireSuperAdmin>
            ),
          },
          { path: "/operators/:id", element: <OperatorDetailPage /> },
          // Payments observability (PR-5; read-only).
          { path: "/payment-intents", element: <PaymentIntentsListPage /> },
          { path: "/payment-intents/:id", element: <PaymentIntentDetailPage /> },
          { path: "/inbound-payments", element: <InboundPaymentsListPage /> },
          { path: "/inbound-payments/:id", element: <InboundPaymentDetailPage /> },
          // Operational observability (PR-6a; read-only).
          { path: "/callback-inbox", element: <CallbackInboxListPage /> },
          { path: "/callback-inbox/:id", element: <CallbackInboxDetailPage /> },
          { path: "/webhook-outbox", element: <WebhookOutboxListPage /> },
          { path: "/webhook-outbox/:id", element: <WebhookOutboxDetailPage /> },
          {
            path: "/audit-log",
            element: (
              <RequireSuperAdmin>
                <AuditLogListPage />
              </RequireSuperAdmin>
            ),
          },
          { path: "/balance-queries", element: <BalanceQueriesListPage /> },
          { path: "/balance-queries/:id", element: <BalanceQueryDetailPage /> },
        ],
      },
      { path: "*", element: <Navigate to="/" replace /> },
    ],
  },
]);

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <TooltipProvider delayDuration={150}>
          <AuthProvider>
            {/*
              CommandPaletteProvider is mounted inside AppLayout so the
              ⌘K shortcut + palette only attach for authenticated routes.
              (PR #136 review: don't bind a global keyboard listener on
              /login, /change-password, /totp/*.)
            */}
            <RouterProvider router={router} />
            <Toaster position="top-right" richColors closeButton />
          </AuthProvider>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
